import {
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import {
  feeSelectedDaysSelector,
  infoSelector,
  setBusinessBalance,
  setBusinessPcBalance,
  setFeeSelectedDays,
  setInfo,
} from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useFeeHandlers(
  info: MyBusiness | GetBusinessInfoResponse,
  device: DeviceEnum,
) {
  const { t, i18n } = useTranslation("business");
  const dispatch = useDispatch();

  const [isPending, setIsPending] = useState(false);

  const selectedDays = useAppSelector(feeSelectedDaysSelector);
  const tabletInfo = useAppSelector(infoSelector);

  const setSelectedDays = useCallback(
    (days: number) => {
      dispatch(setFeeSelectedDays(days));
    },
    [dispatch],
  );

  const getFeeAmountBySelectedDays = useCallback(() => {
    if (!info) return null;
    const maxPayAmount = info.maxFee - info.feeBalance;
    const payAmount = selectedDays * info.totalFee;

    if (payAmount > maxPayAmount) return maxPayAmount;
    if (payAmount < 0) return 0;
    return payAmount;
  }, [info, selectedDays]);

  const updateEstateBalance = useCallback(async () => {
    if (!info) return;

    try {
      const response = await BusinessMpProvider.getInfo(info.id);

      if (response instanceof RpcMpProviderSuccessResponse) {
        if (device === DeviceEnum.TABLET) {
          dispatch(setBusinessBalance(response.data.feeBalance));
        } else {
          dispatch(setBusinessPcBalance(response.data.feeBalance));
          if (info.id === tabletInfo?.id) {
            dispatch(setInfo({ ...tabletInfo, feeBalance: response.data.feeBalance }));
          }
        }
      } else if (
        response?.error?.message &&
        (i18n.exists(`business:business.errors.${response.error.message}`) ||
          i18n.exists(`business.errors.${response.error.message}`))
      ) {
        console.error(response.error.message);
        toast.error(t(`business.errors.${response.error.message}`));
      } else {
        console.error(response?.error?.message ?? "Unknown RPC error");
        toast.error(t("business.toast.infoLoadError"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("business.toast.infoLoadError"));
    }
  }, [device, dispatch, i18n, info, t, tabletInfo]);

  const payFeeHandler = useCallback(async () => {
    if (!info) return;

    try {
      setIsPending(true);
      const amount = getFeeAmountBySelectedDays();
      if (!amount) {
        toast.error(t("business.toast.feeCantPay"));
        return;
      }

      const response = await BusinessMpProvider.payBusinessFee(info.id, amount);

      if (response instanceof RpcMpProviderSuccessResponse) {
        setSelectedDays(0);
        await updateEstateBalance();
      } else if (
        response?.error?.message &&
        (i18n.exists(`business:business.errors.${response.error.message}`) ||
          i18n.exists(`business.errors.${response.error.message}`))
      ) {
        console.error(response.error.message);
        toast.error(t(`business.errors.${response.error.message}`));
      } else {
        console.error(response?.error?.message ?? "Unknown RPC error");
        toast.error(t("business.toast.feeCantPay"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("business.errors.PAY_FEE_SYSTEM_ERROR"));
    } finally {
      setIsPending(false);
    }
  }, [getFeeAmountBySelectedDays, i18n, info, setSelectedDays, t, updateEstateBalance]);

  return {
    info,
    payFeeHandler,
    isPending,
    selectedDays,
    setSelectedDays,
    getFeeAmountBySelectedDays,
  };
}
